/* eslint-disable vue/one-component-per-file */
import {
  App,
  Component,
  defineComponent,
  h,
  inject,
  InjectionKey,
  nextTick,
  PropType,
  provide,
  Ref,
  ref,
  ShallowReactive,
  shallowReactive,
  Transition,
  VNodeChild,
} from "vue";
import ToastTextIcon from "./reader/ToastTextIcon.vue";
import { invoke } from "@vueuse/core";

export function provideUILayer(app: App) {
  const uiLayers = shallowReactive<UILayer[]>([]);

  let key: number = 1;

  const add = <Props extends Record<string, unknown>>(
    component: Component<Props>,
    props: Props,
    options?: { overlay?: OverlayType; transition?: TransitionType },
    slots?: Record<string, () => VNodeChild>,
    onClose?: (source?: CloseSource) => void | Promise<void>
  ) => {
    const ready = ref(false);

    const layer = {
      key: key++,
      component,
      props,
      overlay: options?.overlay ?? OverlayType.Normal,
      transition: options?.transition ?? TransitionType.SlideLeft,
      ready,
      slots,
      onClose,
    } as UILayer<Props>;

    uiLayers.push(layer);
    nextTick(() => (ready.value = true));

    return (source: CloseSource = CloseSource.Other) =>
      remove(uiLayers, layer, source);
  };

  app.provide(uiLayersKey, {
    add,
    showToast: <Props extends Record<string, unknown>>(
      content: Component<Props>,
      props: Props,
      icon: string,
      options?: ToastOptions
    ) =>
      add(
        ToastTextIcon,
        {
          icon,
          timer: options?.timeout ?? 5000,
          closeAction: options?.closeAction,
        },
        {
          overlay: options?.overlay ?? OverlayType.None,
          transition: TransitionType.SlideLeftMobileUp,
        },
        {
          default: () => h(content, props),
        },
        options?.onClose
      ),
    layers: uiLayers,
  });
}

export enum OverlayType {
  None,
  Normal,
  Transparent,
}

export enum TransitionType {
  SlideLeft = "slide-left",
  SlideSidebarLeft = "slide-sidebar-left",
  SlideLeftMobileUp = "slide-left-mobile-up",
}

export function useUILayers(): UseUILayer {
  const uiLayers = inject(uiLayersKey);
  if (!uiLayers) {
    throw new Error("useUILayer must be used within a provideUILayer");
  }

  const activeLayer = inject(activeLayerKey, null) as UILayer | null;

  return {
    add: uiLayers.add,
    close: activeLayer
      ? (source: CloseSource = CloseSource.Other) =>
          remove(uiLayers.layers, activeLayer, source)
      : undefined,
    showToast: uiLayers.showToast,
  };
}

function remove(layers: UILayer[], layer: UILayer, source: CloseSource) {
  // TODO: trigger onClose callbacks
  layer.ready.value = false;
  nextTick(() => {
    const index = layers.indexOf(layer);
    if (index !== -1) {
      layers.splice(index, 1);
    }
    invoke(() => layer.onClose?.(source));
  });
}

const LayerWrapper = defineComponent({
  name: "LayerWrapper",
  props: {
    layer: {
      type: Object as PropType<UILayer>,
      required: true,
    },
  },
  setup({ layer }) {
    const { layers } = inject(uiLayersKey)!;
    provide(activeLayerKey, layer);

    const overlayClose = () => remove(layers, layer, CloseSource.Overlay);

    return () => {
      const slot = () =>
        layer.ready.value ? h(layer.component, layer.props, layer.slots) : [];
      return [
        overlay(layer.overlay, overlayClose),
        transition(layer.transition, slot),
      ];
    };
  },
});

export default defineComponent({
  name: "UILayers",
  setup() {
    const uiLayers = inject(uiLayersKey);
    if (!uiLayers) {
      throw new Error("useUILayer must be used within a provideUILayer");
    }

    const { layers } = uiLayers;

    return () =>
      layers.flatMap((layer) => h(LayerWrapper, { layer, key: layer.key }));
  },
});

function overlay(
  type: OverlayType,
  close: (ev: MouseEvent) => void
): VNodeChild {
  if (type === OverlayType.None) {
    return [];
  }

  let classes = "overlay";
  if (type === OverlayType.Transparent) {
    classes += " overlay--transparent";
  }
  return h("div", {
    class: classes,
    onClick: close,
  });
}

function transition(name: TransitionType, slot: () => VNodeChild): VNodeChild {
  return h(Transition, { name }, { default: slot });
}

const activeLayerKey = Symbol("activeLayerKey") as InjectionKey<UILayer | null>;
const uiLayersKey = Symbol("uiLayersKey") as InjectionKey<
  UseUILayer & { layers: ShallowReactive<UILayer[]> }
>;

type UILayer<Props extends Record<string, unknown> = Record<string, unknown>> =
  {
    key: number;
    component: Component<Props>;
    props: Props;
    overlay: OverlayType;
    transition: TransitionType;
    ready: Ref<boolean>;
    slots?: Record<string, () => VNodeChild>;
    onClose?: (source?: CloseSource) => void | Promise<void>;
  };

export enum CloseSource {
  Overlay,
  CloseButton,
  Other,
}

type LayerOptions = { overlay?: OverlayType; transition?: TransitionType };

type ToastOptions = {
  timeout?: number;
  closeAction?: boolean;
  overlay?: OverlayType;
  onClose?: (source?: CloseSource) => void | Promise<void>;
};

type LayerCloser = (source?: CloseSource) => void;

type UseUILayer = {
  add: <Props extends Record<string, unknown>>(
    component: Component<Props>,
    props: Props,
    options?: LayerOptions
  ) => LayerCloser;
  close?: LayerCloser;
  showToast: <Props extends Record<string, unknown>>(
    content: Component<Props>,
    props: Props,
    icon: string,
    options?: ToastOptions
  ) => LayerCloser;
};
